import { generatePath, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import ModalDialog from 'src/components/ModalDialog';
import { useForm } from 'src/components/Form';
import { RequestMethod, pepApiRequest, useStatefulApi } from 'src/services';
import { isSpectroFeatureEnabled } from 'src/services/featureFlagsService';
import { useFeatureFlagsContext } from 'src/contexts/FeatureFlagsContext';
import { useStoreUnitActions } from 'src/redux/helpers';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import solutionsStore, { useCurrentSiteName } from '../../redux';
import { getAddClusterFormFields } from '../formFields';
import {
  API_CREATE_CLUSTER,
  API_SITE_CLUSTERS,
  API_CLUSTER_INSTANCES,
} from '../../constants';
import { SiteDeviceProjectItem, SiteClusterItemField, SiteClusterProviderType } from '../../types';

type Props = {
  title: string;
  setCluseterDetail: (res) => void;
  setNameSpaces: (res) => void;
  setLoading: (loading: boolean) => void;
  open: boolean;
  onClose: () => void;
};

type ClusterInstance = {
  id: string;
  instanceName: string;
  providerType: string;
  url: string;
  keyVaultPasswordKey: string;
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  modifiedDate: string;
};

function SiteClusterCreateModal({
  title,
  setCluseterDetail,
  setLoading,
  setNameSpaces,
  open,
  onClose,
}: Props) {
  const siteName = useCurrentSiteName();
  const { siteId } = useParams();
  const { showAlert } = useAlert();
  const actions = useStoreUnitActions(solutionsStore);
  const [instanceOptions, setInstanceOptions] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [providerType, setProviderType] = useState<string>(SiteClusterProviderType.Rancher);
  
  // Fetch feature flags
  const { flags: featureFlags, loading: flagsLoading } = useFeatureFlagsContext();
  const isSpectroEnabled = isSpectroFeatureEnabled(featureFlags);

  const [onSubmit, loading] = useStatefulApi(
    (params) =>
      pepApiRequest({
        method: RequestMethod.Post,
        url: generatePath(API_CREATE_CLUSTER, { siteId }),
        params: { ...params, authType: 'SPN' },
      }),
    (res, params) => {
      showAlert({
        type: AlertType.Success,
        title: 'New Cluster Created',
        text: `${params.name} is created successfully for site ${siteName}`,
      });
      onClose();
      const nameSpaces: SiteDeviceProjectItem[] = [];
      setLoading(true);
      pepApiRequest({
        method: RequestMethod.Get,
        url: generatePath(API_SITE_CLUSTERS, { siteId }),
        skipErrorAlert: true,
        skipErrorCodes: [500],
      }).then((response) => {
        setCluseterDetail(response);
        setLoading(false);
        const nameSpaces: SiteDeviceProjectItem[] = [];
        response?.clustersDetail?.map((clusters: any) =>
          clusters.nodeNamespaces?.Namespaces.map((namespace) =>
            nameSpaces.push({ nameSpace: namespace })
          )
        );
        setNameSpaces(nameSpaces);
      });
    }
  );

  const fetchInstances = async (type: string) => {
    setLoadingInstances(true);
    try {
      const response: ClusterInstance[] = await pepApiRequest({
        method: RequestMethod.Get,
        url: API_CLUSTER_INSTANCES,
        queryParams: { providerType: type },
      });
      const options = response.map((instance) => ({
        label: instance.instanceName,
        value: instance.id,
      }));
      setInstanceOptions(options);
    } catch (error) {
      console.error('Error fetching instances:', error);
      setInstanceOptions([]);
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleProviderTypeChange = useCallback((event) => {
    const newProviderType = event.target.value;
    setProviderType(newProviderType);
  }, []);

  const fields = useMemo(
    () =>
      getAddClusterFormFields(
        instanceOptions,
        loadingInstances,
        handleProviderTypeChange,
        providerType,
        isSpectroEnabled
      ),
    [instanceOptions, loadingInstances, handleProviderTypeChange, providerType, isSpectroEnabled]
  );

  const [form, handleSubmit, setFieldValue] = useForm({
    fields,
    onSubmit,
  });

  useEffect(() => {
    setFieldValue(SiteClusterItemField.InstanceId, '');
    setInstanceOptions([]);
    fetchInstances(providerType);
  }, [providerType, setFieldValue]);

  return (
    <ModalDialog
      open={open}
      title={title}
      loading={loading}
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmBtnText="Add"
      maxWidth="xs"
      fullWidth
    >
      {form}
    </ModalDialog>
  );
}

export default SiteClusterCreateModal;
